import AsyncStorage from '@react-native-async-storage/async-storage';

const STORAGE_KEYS = {
  PLAN_VERSIONS: 'planVersions',
  LATEST_CHECKIN: 'latestCheckIn',
  SYNC_METADATA: 'syncMetadata',
};

/**
 * Service to manage plan versions and check-ins in local storage
 * Handles syncing with server and detecting plan changes
 */
class LocalStorageService {
  /**
   * Store plan versions and check-in in local storage
   * @param {Array} planVersions - Array of plan versions
   * @param {Object} latestCheckIn - Latest check-in object
   */
  static async storePlanData(planVersions, latestCheckIn) {
    try {
      const timestamp = new Date().toISOString();
      
      // Store plan versions
      await AsyncStorage.setItem(
        STORAGE_KEYS.PLAN_VERSIONS,
        JSON.stringify({
          data: planVersions,
          lastSynced: timestamp,
          latestVersionId: planVersions.length > 0 ? planVersions[0]._id : null,
          latestVersionNumber: planVersions.length > 0 ? planVersions[0].versionNumber : null,
        })
      );

      // Store latest check-in
      await AsyncStorage.setItem(
        STORAGE_KEYS.LATEST_CHECKIN,
        JSON.stringify({
          data: latestCheckIn,
          lastSynced: timestamp,
          checkInId: latestCheckIn?._id || null,
        })
      );

      // Update sync metadata
      await AsyncStorage.setItem(
        STORAGE_KEYS.SYNC_METADATA,
        JSON.stringify({
          lastSyncTime: timestamp,
          planVersionCount: planVersions.length,
        })
      );

      console.log('Plan data stored in local storage:', {
        planVersions: planVersions.length,
        hasCheckIn: !!latestCheckIn,
        timestamp,
      });
    } catch (error) {
      console.error('Error storing plan data:', error);
      throw error;
    }
  }

  /**
   * Get stored plan versions from local storage
   * @returns {Object|null} Stored plan versions with metadata
   */
  static async getStoredPlanVersions() {
    try {
      const stored = await AsyncStorage.getItem(STORAGE_KEYS.PLAN_VERSIONS);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error getting stored plan versions:', error);
      return null;
    }
  }

  /**
   * Get stored latest check-in from local storage
   * @returns {Object|null} Stored check-in with metadata
   */
  static async getStoredLatestCheckIn() {
    try {
      const stored = await AsyncStorage.getItem(STORAGE_KEYS.LATEST_CHECKIN);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error getting stored check-in:', error);
      return null;
    }
  }

  /**
   * Get all stored data (plan versions and check-in)
   * @returns {Object} Combined stored data
   */
  static async getAllStoredData() {
    try {
      const [planVersions, latestCheckIn] = await Promise.all([
        this.getStoredPlanVersions(),
        this.getStoredLatestCheckIn(),
      ]);

      return {
        planVersions: planVersions?.data || [],
        latestCheckIn: latestCheckIn?.data || null,
        metadata: {
          planVersionsLastSynced: planVersions?.lastSynced || null,
          checkInLastSynced: latestCheckIn?.lastSynced || null,
          latestVersionId: planVersions?.latestVersionId || null,
          latestVersionNumber: planVersions?.latestVersionNumber || null,
        },
      };
    } catch (error) {
      console.error('Error getting all stored data:', error);
      return {
        planVersions: [],
        latestCheckIn: null,
        metadata: {},
      };
    }
  }

  /**
   * Check if plan has changed by comparing with stored data
   * @param {Array} serverPlanVersions - Plan versions from server
   * @returns {Object} Comparison result with hasChanged flag
   */
  static async checkPlanChanged(serverPlanVersions) {
    try {
      const stored = await this.getStoredPlanVersions();
      
      if (!stored || !stored.data || stored.data.length === 0) {
        return {
          hasChanged: true,
          reason: 'No stored data found',
        };
      }

      const serverLatestVersion = serverPlanVersions.length > 0 ? serverPlanVersions[0] : null;
      
      if (!serverLatestVersion) {
        return {
          hasChanged: false,
          reason: 'No server plan versions',
        };
      }

      // Check if latest version ID has changed
      if (stored.latestVersionId !== serverLatestVersion._id) {
        return {
          hasChanged: true,
          reason: 'New plan version detected',
          oldVersionId: stored.latestVersionId,
          newVersionId: serverLatestVersion._id,
          oldVersionNumber: stored.latestVersionNumber,
          newVersionNumber: serverLatestVersion.versionNumber,
        };
      }

      // Check if version number has changed (backup check)
      if (stored.latestVersionNumber !== serverLatestVersion.versionNumber) {
        return {
          hasChanged: true,
          reason: 'Plan version number changed',
          oldVersionNumber: stored.latestVersionNumber,
          newVersionNumber: serverLatestVersion.versionNumber,
        };
      }

      return {
        hasChanged: false,
        reason: 'No changes detected',
      };
    } catch (error) {
      console.error('Error checking plan changed:', error);
      return {
        hasChanged: true,
        reason: 'Error during comparison',
        error: error.message,
      };
    }
  }

  /**
   * Check if check-in has changed
   * @param {Object} serverCheckIn - Latest check-in from server
   * @returns {Object} Comparison result
   */
  static async checkCheckInChanged(serverCheckIn) {
    try {
      const stored = await this.getStoredLatestCheckIn();
      
      if (!stored || !stored.data) {
        return {
          hasChanged: !!serverCheckIn,
          reason: serverCheckIn ? 'New check-in available' : 'No stored check-in',
        };
      }

      if (!serverCheckIn) {
        return {
          hasChanged: false,
          reason: 'No server check-in',
        };
      }

      // Check if check-in ID has changed (new check-in)
      if (stored.checkInId !== serverCheckIn._id) {
        return {
          hasChanged: true,
          reason: 'New check-in detected',
          oldCheckInId: stored.checkInId,
          newCheckInId: serverCheckIn._id,
        };
      }

      return {
        hasChanged: false,
        reason: 'Same check-in',
      };
    } catch (error) {
      console.error('Error checking check-in changed:', error);
      return {
        hasChanged: true,
        reason: 'Error during comparison',
      };
    }
  }

  /**
   * Format data for Python API consumption
   * Structures the data in a clean format suitable for sending to Python API
   * @returns {Object} Formatted data for Python API
   */
  static async getDataForPythonAPI() {
    try {
      const storedData = await this.getAllStoredData();
      
      // Format plan versions for Python API
      const formattedPlanVersions = storedData.planVersions.map(plan => ({
        id: plan._id,
        versionNumber: plan.versionNumber,
        generatedBy: plan.generatedBy,
        createdAt: plan.createdAt,
        isActive: plan.isActive,
        reasonForChange: plan.reasonForChange,
        workoutPlan: plan.workoutPlan,
        dietPlan: plan.dietPlan,
        macroTargets: plan.macroTargets,
        googleSheetId: plan.googleSheetId,
        motivationalImageUrl: plan.motivationalImageUrl,
      }));

      // Format check-in for Python API
      const formattedCheckIn = storedData.latestCheckIn ? {
        id: storedData.latestCheckIn._id,
        date: storedData.latestCheckIn.date,
        weight: storedData.latestCheckIn.weight,
        sleep: storedData.latestCheckIn.sleep,
        stress: storedData.latestCheckIn.stress,
        digestion: storedData.latestCheckIn.digestion,
        soreness: storedData.latestCheckIn.soreness,
        energy: storedData.latestCheckIn.energy,
        mood: storedData.latestCheckIn.mood,
        notes: storedData.latestCheckIn.notes,
        photoUrl: storedData.latestCheckIn.photoUrl,
        photos: storedData.latestCheckIn.photos,
        planChangeRequest: storedData.latestCheckIn.planChangeRequest,
        status: storedData.latestCheckIn.status,
        createdAt: storedData.latestCheckIn.createdAt,
      } : null;

      return {
        planVersions: formattedPlanVersions,
        latestCheckIn: formattedCheckIn,
        metadata: {
          ...storedData.metadata,
          exportedAt: new Date().toISOString(),
        },
      };
    } catch (error) {
      console.error('Error formatting data for Python API:', error);
      throw error;
    }
  }

  /**
   * Clear all stored plan data
   */
  static async clearStoredData() {
    try {
      await Promise.all([
        AsyncStorage.removeItem(STORAGE_KEYS.PLAN_VERSIONS),
        AsyncStorage.removeItem(STORAGE_KEYS.LATEST_CHECKIN),
        AsyncStorage.removeItem(STORAGE_KEYS.SYNC_METADATA),
      ]);
      console.log('Stored plan data cleared');
    } catch (error) {
      console.error('Error clearing stored data:', error);
      throw error;
    }
  }

  /**
   * Get sync metadata
   * @returns {Object} Sync metadata
   */
  static async getSyncMetadata() {
    try {
      const stored = await AsyncStorage.getItem(STORAGE_KEYS.SYNC_METADATA);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error getting sync metadata:', error);
      return null;
    }
  }
}

export default LocalStorageService;

